import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import TableContainer from "../TableContainer/TableContainer";
import { Box, TablePagination } from "@mui/material";
import { MdSettingsBackupRestore } from "react-icons/md";
import { loader, snackbar } from "../../utils";
import axios from "axios";
import { baseUrl } from "../service/Api";

const DeletedUsersList = () => {
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);

  const getDeletedUsers = async () => {
    try {
      loader.start();
      const response = await axios.get(baseUrl + "/deleteNewSubscriber/list");

      if (response?.data?.error === false) {
        const users = response.data.data.users || [];

        // Sort by updatedAt (most recent first)
        users.sort((a, b) => {
          const aDate = new Date(a?.updatedAt);
          const bDate = new Date(b?.updatedAt);
          return bDate - aDate;
        });

        setAllData(users);
        setFilteredData(users);
        setTotalData(users?.length || 0);
        paginate(users, 0);
      } else {
        snackbar.error(
          response?.data?.errormessage || "Failed to fetch deleted users",
        );
      }
    } catch (error) {
      console.error("Error fetching deleted users:", error);
      snackbar.error(
        error?.response?.data?.errormessage ||
          error?.message ||
          "Failed to fetch deleted users",
      );
    } finally {
      loader.stop();
    }
  };

  function filterData(searchValue) {
    setSearchInput(searchValue);
    setPage(0);

    if (!searchValue.trim()) {
      setFilteredData(allData);
      setTotalData(allData?.length || 0);
      paginate(allData, 0);
      return;
    }

    const filtered = allData.filter(
      (res) =>
        (res.name &&
          res.name.toLowerCase().includes(searchValue.toLowerCase())) ||
        (res.mobile_number && res.mobile_number.includes(searchValue)) ||
        (res.email &&
          res.email.toLowerCase().includes(searchValue.toLowerCase())) ||
        (res.subscriberId &&
          res.subscriberId.toLowerCase().includes(searchValue.toLowerCase())) ||
        (res.activeDevices &&
          res.activeDevices.some((device) =>
            device.deviceId?.toLowerCase().includes(searchValue.toLowerCase()),
          )),
    );

    setFilteredData(filtered);
    setTotalData(filtered?.length || 0);
    paginate(filtered, 0);
  }

  function paginate(dataArray, cpage) {
    const startIndex = cpage * rowsPerPage;
    const slicedData = dataArray.slice(startIndex, startIndex + rowsPerPage);
    setData(slicedData);
  }

  useEffect(() => {
    getDeletedUsers();
  }, []);

  useEffect(() => {
    setPage(0);
    paginate(filteredData, 0);
  }, [rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    paginate(filteredData, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetDevice = async (subscriberId, userName) => {
    if (!subscriberId) {
      snackbar.error("Subscriber ID is required");
      return;
    }

    const confirmReset = window.confirm(
      `Are you sure you want to reset the device for ${userName || "this user"}?`,
    );

    if (!confirmReset) return;

    try {
      loader.start();
      const payload = { subscriberId };
      const response = await axios.post(
        baseUrl + "/deleteNewSubscriber/resetDevice",
        payload,
      );

      if (response?.data?.error === false) {
        snackbar.success("Device reset successful!");
        getDeletedUsers(); // Refresh the list
      } else {
        snackbar.error(
          response?.data?.errormessage ||
            "Device reset failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Reset device error:", error);
      snackbar.error(
        error?.response?.data?.errormessage ||
          error?.message ||
          "Device reset failed. Please try again.",
      );
    } finally {
      loader.stop();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <Box>
        <h2 className="fw-600">Deleted Users List</h2>
        <div className={`row mt-4`}>
          <div className={`col-lg-5 col-md-6 col-sm-12 col-12`}>
            <input
              type="search"
              placeholder="Search by name, mobile, email, subscriber ID, or device ID"
              className="form-control w-100"
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                if (value === " ") {
                  filterData("");
                } else {
                  filterData(value);
                }
              }}
            />
          </div>
        </div>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <span className="fw-bold">Sr. No.</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Name</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Mobile Number</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Email</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Device IDs</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Deleted At</span>
                </TableCell>
                <TableCell>
                  <span className="fw-bold">Action</span>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data && data.length > 0 ? (
                data.map((row, index) => {
                  return (
                    <TableRow key={row._id || index}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.name || "N/A"}</TableCell>
                      <TableCell>{row.mobile_number || "N/A"}</TableCell>
                      <TableCell>{row.email || "N/A"}</TableCell>
                      <TableCell>
                        {row.activeDevices && row.activeDevices.length > 0 ? (
                          <div style={{ fontSize: "12px" }}>
                            {row.activeDevices.map((device, idx) => (
                              <div key={idx} style={{ marginBottom: "2px" }}>
                                {device.deviceId}
                              </div>
                            ))}
                          </div>
                        ) : (
                          "No active devices"
                        )}
                      </TableCell>
                      <TableCell>{formatDate(row.updatedAt)}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<MdSettingsBackupRestore />}
                          onClick={() =>
                            handleResetDevice(row.subscriberId, row.name)
                          }
                          disabled={!row.subscriberId}
                        >
                          Reset Device
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No deleted users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalData}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </>
  );
};

export default DeletedUsersList;
